import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OfferService } from '../../_services/offer.service';
import { User } from '../../_models/user';
import { ToastrService } from 'ngx-toastr';

@Component({
    selector: 'app-sale-detail',
    templateUrl: './sale-detail.component.html',
    styleUrls: ['./sale-detail.component.css'],
    standalone: false
})
export class SaleDetailComponent implements OnInit {
    sale: any = null;
    loading = true;
    error = false;
    saleId: number = 0;
    user: User;
    selectedStatus = '';
    description = '';
    updating = false;

    constructor(
        private offerService: OfferService,
        private route: ActivatedRoute,
        private router: Router,
        private toastr: ToastrService
    ) {
        this.user = JSON.parse(localStorage.getItem('currentUser') ?? '{}');
    }

    ngOnInit(): void {
        this.saleId = Number(this.route.snapshot.paramMap.get('saleId'));
        if (this.saleId) {
            this.loadSale();
        } else {
            this.error = true;
            this.loading = false;
        }
    }

    loadSale(): void {
        this.loading = true;
        this.error = false;
        this.offerService.getSale(this.saleId).subscribe({
            next: (response: any) => {
                this.sale = response?.data || response;
                this.selectedStatus = this.sale.status || '';
                this.loading = false;
            },
            error: () => {
                this.sale = null;
                this.error = true;
                this.loading = false;
            }
        });
    }

    updateStatus(): void {
        if (!this.selectedStatus || this.updating) return;
        this.updating = true;
        const payload: any = { status: this.selectedStatus };
        if (this.description) { payload.description = this.description; }
        this.offerService.updateSaleStatus(this.saleId, payload).subscribe({
            next: (res: any) => {
                this.updating = false;
                this.description = '';
                this.sale.status = this.selectedStatus;
                this.toastr.success(res?.message || 'Status updated successfully');
            },
            error: () => {
                this.updating = false;
                this.toastr.error('Failed to update status');
            }
        });
    }

    getUpdateStatusOptions(): string[] {
        if (this.sale?.localPickup) {
            return ['VALIDATED', 'DELIVERED'];
        }
        return ['VALIDATED', 'IN_PREPARATION', 'SHIPPED', 'CANCELLED'];
    }

    getStatusClass(status: string): string {
        switch (status?.toUpperCase()) {
            case 'PENDING': return 'badge bg-warning text-dark';
            case 'VALIDATED': return 'badge bg-success';
            case 'IN_PREPARATION': return 'badge bg-info';
            case 'SHIPPED': return 'badge bg-primary';
            case 'DELIVERED': return 'badge bg-success';
            case 'CANCELLED': return 'badge bg-danger';
            default: return 'badge bg-secondary';
        }
    }

    getStatusLabel(status: string): string {
        if (!status) return 'sales.status_pending';
        return 'sales.status_' + status.toLowerCase();
    }

    getLogisticStatusLabel(status: string): string {
        if (!status) return '';
        return 'delivery.status_' + status.toLowerCase();
    }

    isCurrentStatusChangeable(): boolean {
        return ['VALIDATED', 'IN_PREPARATION', 'SHIPPED', 'CANCELLED']
            .includes(this.sale?.status?.toUpperCase());
    }

    goBack(): void {
        this.router.navigate(['/user', this.user.id, 'sales']);
    }

    getCustomerName(): string {
        if (!this.sale?.customer) {
            return '-';
        }
        const firstName = this.sale.customer.firstName || '';
        const lastName = this.sale.customer.lastName || '';
        return `${firstName} ${lastName}`.trim() || '-';
    }

    getCustomerPhone(): string {
        return this.sale?.customer?.phone || '-';
    }

    getCustomerEmail(): string {
        return this.sale?.customer?.email || '-';
    }

    getCustomerCity(): string {
        return this.sale?.customer?.city || '-';
    }

    formatDate(dateString: string): string {
        if (!dateString) {
            return '-';
        }
        return new Date(dateString).toLocaleString();
    }
}
